import { endOfDay, startOfDay, subDays } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import AnalyticsItem from '@/components/BuildingDashboard/Rewards/components/AnalyticsItem/AnalyticsItem'
import { getGiftCardsAnalyticsMapping } from '@/components/BuildingDashboard/Rewards/helpers'
import Icon from '@/components/Icons'
import Upselling from '@/components/Upselling/Upselling'
import { useDesignVersionToggle } from '@/hooks'
import { selectBuildingsForFilter } from '@/reducers/buildingSelectorReducer'
import { fetchRewardsAnalytics, selectRewardsAnalytics } from '@/reducers/rewardsReducer'
import { selectUserRole } from '@/reducers/userDataReducer'

import * as S from './gift-cards.page.styled'
import { GiftCardsDashboard } from './gift-cards-dashboard'

const DEFAULT_DAYS_RANGE = 30
const INITIAL_DATE_FILTER: [Date | null, Date | null] = [
  subDays(startOfDay(Date.now()), DEFAULT_DAYS_RANGE),
  startOfDay(Date.now()),
]

export const GiftCardsPage = () => {
  const { t } = useTranslation(['gift-cards', 'rewards-dashboard'])
  const { buildingId } = useParams<{ buildingId?: string }>()
  // biome-ignore lint/suspicious/noExplicitAny: legacy untyped Redux dispatch (matches RewardsTable.jsx)
  const dispatch = useDispatch<any>()
  const buildings = useSelector(selectBuildingsForFilter)
  const analytics = useSelector(selectRewardsAnalytics)
  const userRole = useSelector(selectUserRole)
  const [showAnalytics, setShowAnalytics] = useState(true)
  const [dateFilter, setDateFilter] = useState<[Date | null, Date | null]>(INITIAL_DATE_FILTER)

  const selectedBuilding = useMemo(
    () => buildings.find((building) => building.id === Number(buildingId)) ?? null,
    [buildings, buildingId],
  )

  const { redirect } = useDesignVersionToggle({
    group: 'gift-cards',
    screen: 'GiftCards',
    to: {
      params: { buildingId: buildingId ?? '' },
      path: '/dashboard/building-dashboard/rewards/gift-cards/legacy/:buildingId',
    },
    version: 'new',
  })

  useEffect(() => {
    const [startDate, endDate] = dateFilter
    if (!buildingId || !startDate || !endDate) return
    void dispatch(
      fetchRewardsAnalytics({
        buildingId,
        endDate: fromZonedTime(endOfDay(endDate), 'UTC').getTime(),
        startDate: fromZonedTime(startDate, 'UTC').getTime(),
      }),
    )
  }, [buildingId, dateFilter])

  useEffect(() => {
    setDateFilter(INITIAL_DATE_FILTER)
  }, [buildingId])

  if (redirect) return redirect

  if (!selectedBuilding?.rewardsAvailable && userRole !== 'Administrator') {
    return (
      <div className="rewards-container">
        <Upselling
          params={{ type: 'giftCards' }}
          containerStyle={{ height: 'calc(100vh - 320px)' }}
        />
      </div>
    )
  }

  return (
    <>
      {analytics && selectedBuilding && (
        <div className="rewards-container">
          <div
            className={`rewards-dashboard rewards-dashboard--giftcards-tab ${showAnalytics ? '' : 'hidden'}`}
          >
            <span className="rewards-dashboard__title">
              {t('rewards-dashboard:giftCardsTitle')}
              <button
                className="button button-black-outline"
                type="button"
                onClick={() => setShowAnalytics(!showAnalytics)}
              >
                <Icon icon={showAnalytics ? 'showEye' : 'hideEye'} />
                {showAnalytics
                  ? t('rewards-dashboard:hideButton')
                  : t('rewards-dashboard:showButton')}
              </button>
            </span>
            <div className="rewards-dashboard__wrapper">
              <AnalyticsItem
                setDateFilter={setDateFilter}
                dateFilter={dateFilter}
                analytics={analytics}
                mapping={getGiftCardsAnalyticsMapping(t)}
              />
            </div>
          </div>
        </div>
      )}
      <S.TableSection id="gift-cards-page-table" className="rewards-container">
        <GiftCardsDashboard building={selectedBuilding} />
      </S.TableSection>
    </>
  )
}
